import { renderIcon, type IconName } from '../../icons';
import './button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonOptions {
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIcon?: IconName;
  trailingIcon?: IconName;
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (ev: MouseEvent) => void;
  ariaLabel?: string;
  fullWidth?: boolean;
}

export interface ButtonHandle {
  el: HTMLButtonElement;
  setLoading: (loading: boolean) => void;
  setDisabled: (disabled: boolean) => void;
  setLabel: (label: string) => void;
  destroy: () => void;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

function createSpinner(): SVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'tkos-btn__spinner');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('aria-hidden', 'true');
  const circle = document.createElementNS(SVG_NS, 'circle');
  circle.setAttribute('cx', '8');
  circle.setAttribute('cy', '8');
  circle.setAttribute('r', '6');
  svg.appendChild(circle);
  return svg;
}

export function createButton(opts: ButtonOptions): ButtonHandle {
  const {
    label,
    variant = 'primary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    loading = false,
    disabled = false,
    type = 'button',
    onClick,
    ariaLabel,
    fullWidth = false,
  } = opts;

  const btn = document.createElement('button');
  btn.type = type;
  btn.className = [
    'tkos-btn',
    `tkos-btn--${variant}`,
    `tkos-btn--${size}`,
    fullWidth ? 'tkos-btn--full' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (ariaLabel) btn.setAttribute('aria-label', ariaLabel);

  let leadingEl: HTMLSpanElement | null = null;
  if (leadingIcon) {
    leadingEl = document.createElement('span');
    leadingEl.className = 'tkos-btn__icon tkos-btn__icon--leading';
    leadingEl.appendChild(renderIcon(leadingIcon, { size: 'sm' }));
    btn.appendChild(leadingEl);
  }

  const labelEl = document.createElement('span');
  labelEl.className = 'tkos-btn__label';
  labelEl.textContent = label;
  btn.appendChild(labelEl);

  let trailingEl: HTMLSpanElement | null = null;
  if (trailingIcon) {
    trailingEl = document.createElement('span');
    trailingEl.className = 'tkos-btn__icon tkos-btn__icon--trailing';
    trailingEl.appendChild(renderIcon(trailingIcon, { size: 'sm' }));
    btn.appendChild(trailingEl);
  }

  const spinner = createSpinner();

  let isLoading = false;
  let isDisabled = false;

  const applyLoading = (next: boolean): void => {
    isLoading = next;
    if (next) {
      btn.setAttribute('data-loading', 'true');
      btn.setAttribute('aria-busy', 'true');
      if (!spinner.isConnected) btn.appendChild(spinner);
    } else {
      btn.removeAttribute('data-loading');
      btn.removeAttribute('aria-busy');
      if (spinner.isConnected) spinner.remove();
    }
    btn.disabled = isDisabled || isLoading;
  };

  const applyDisabled = (next: boolean): void => {
    isDisabled = next;
    btn.disabled = isDisabled || isLoading;
  };

  applyLoading(loading);
  applyDisabled(disabled);

  const clickHandler = (ev: MouseEvent): void => {
    if (isLoading || isDisabled) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      return;
    }
    onClick?.(ev);
  };

  if (onClick) btn.addEventListener('click', clickHandler);

  return {
    el: btn,
    setLoading: applyLoading,
    setDisabled: applyDisabled,
    setLabel: (next: string): void => {
      labelEl.textContent = next;
    },
    destroy: (): void => {
      if (onClick) btn.removeEventListener('click', clickHandler);
      btn.remove();
    },
  };
}
